<script setup>
import CalendarLayout from "@/Layouts/Calendar.vue";
import TimeSlot from "@/Components/Calendar/Daily/TimeSlot.vue";
//import TimeSlotWeek from "@/Components/Calendar/Daily/TimeSlotWeek.vue";
import { ref, computed, onMounted, onUnmounted } from "vue";

const props = defineProps({
  now: String,
  date: String,
  dateString: String,

  filterView: String, // tag_behandler, tag_raum, woche_behandler
  filterDayCount: Number,
  filterPractitioners: Array,
  filterRooms: Array,

  office: Object,
  allPractitioners: Object,
  allRooms: Object,

  holidays: Array,
  emergencies: Array,
  officeVacations: Array,

  viewData: Object,
  viewType: String, // practioners or rooms
  stati: Array,
  debugtime: String,
});

const hideNames = ref(false);
let nowLineInterval = null;

let earliest = calcEarliest();
let latest = calcLatest();

function calcEarliest() {
  let d = props.viewData[0].date; // Y-m-d
  let earliest = "23:59"; //props.office.earliest; // H:i

  props.viewData.every((view) => {
    let viewDate = new Date(view.earliest);
    if (viewDate < new Date(getDateString(viewDate) + " " + earliest)) {
      earliest = getTimeString(viewDate);
    }
    return true;
  });

  props.emergencies.every((emergency) => {
    if (emergency.date != d) {
      return true;
    }
    let emergencyDate = new Date(emergency.time_start);
    if (
      emergencyDate < new Date(getDateString(emergencyDate) + " " + earliest)
    ) {
      earliest = getTimeString(emergencyDate);
    }
    return true;
  });
  let earliestDate = new Date(d + " " + earliest);
  return earliestDate;
}

function calcLatest() {
  let d = props.viewData[0].date; // Y-m-d
  let latest = "00:00"; //props.office.latest; // H:i

  props.viewData.every((view) => {
    let viewDate = new Date(view.latest);
    if (viewDate > new Date(getDateString(viewDate) + " " + latest)) {
      latest = getTimeString(viewDate);
    }
    return true;
  });

  props.emergencies.every((emergency) => {
    if (emergency.date != d) {
      return true;
    }
    let emergencyDate = new Date(emergency.time_end);
    if (emergencyDate > new Date(getDateString(emergencyDate) + " " + latest)) {
      latest = getTimeString(emergencyDate);
    }
    return true;
  });

  let latestDate = new Date(d + " " + latest);
  return latestDate;
}

const rowCount = computed(() => {
  let interval = props.office.interval; // minutes
  let minutes = (latest - earliest) / 1000 / 60;
  return minutes / interval;
});

function currentRowDate(row) {
  let position = row - 1; // v-for starts at 1
  let interval = props.office.interval; // minutes
  let d = new Date(earliest.getTime());
  return new Date(d.setMinutes(d.getMinutes() + position * interval));
}

function isToday(date) {
  // check if date is today
  return new Date(date).setHours(0, 0, 0, 0) == new Date().setHours(0, 0, 0, 0);
}

function getDateString(date, row) {
  let d = date || currentRowDate(row);
  let year = d.getFullYear();
  let month = ("0" + (d.getMonth() + 1)).slice(-2);
  let day = ("0" + d.getDate()).slice(-2);
  return year + "-" + month + "-" + day;
}

function getTimeString(date, row) {
  let d = date || currentRowDate(row);
  let hours = ("0" + d.getHours()).slice(-2);
  let minutes = ("0" + d.getMinutes()).slice(-2);
  return hours + ":" + minutes;
}

function isFullHour(row) {
  let date = currentRowDate(row);
  return date.getMinutes() === 0;
}

function setNowLine() {
  let elements = document.querySelectorAll(".oteo-now-line");
  // minutes total
  let minutes = (latest - earliest) / 1000 / 60;
  // minutes from earliest today to now
  let now = new Date(); // '2022-12-19 10:00'
  let earliestToday = new Date(
    getDateString(now) + " " + getTimeString(earliest)
  );
  let minutes_now = (now - earliestToday) / 1000 / 60;
  // percentage of now
  let top = (minutes_now / minutes) * 100;
  // set all now lines
  elements.forEach((element) => {
    element.style.top = top + "%";
  });
}

let maxDailyConcurrentCount = ref(0);
function getTimeslots(row, date, colId) {
  let slots = [];
  let crd = currentRowDate(row);
  let viewData = props.viewData.find((viewData) => {
    return viewData.date === date;
  });

  let viewColumns = viewData.viewColumns;
  if (props.filterView != "woche_behandler") {
    // filterView: tag_behandler, tag_raum, woche_behandler
    viewColumns = viewColumns.filter((viewColumn) => {
      return viewColumn.col_id == colId;
    });
  }

  // count concurrent appointments on daily basis
  // and set hasDailyAppointment and hasDailyAppointmentParallel
  let concurrentCount = 0;
  viewColumns.forEach((viewColumn) => {
    // viewColumn = practitioner or room

    let a = false;
    let b = false;
    let tmpslot = null;

    viewColumn.slots.forEach((slot) => {
      let slotDate = new Date(slot.start);
      if (
        slotDate.getHours() === crd.getHours() &&
        slotDate.getMinutes() === crd.getMinutes()
      ) {
        tmpslot = slot;
      }

      if (slot.appointment) {
        a = true;
      }
      if (slot.appointment_parallel) {
        b = true;
      }
    });

    if (a) {
      concurrentCount++;
      tmpslot.hasDailyAppointment = true;
    } else {
      tmpslot.hasDailyAppointment = false;
    }
    if (b) {
      concurrentCount++;
      tmpslot.hasDailyAppointmentParallel = true;
    } else {
      tmpslot.hasDailyAppointmentParallel = false;
    }

    slots.push(tmpslot);
  });

  // append concurrent count
  slots.forEach((slot) => {
    if (props.filterView == "woche_behandler") {
      // filterView: tag_behandler, tag_raum, woche_behandler
      slot.concurrentCount = concurrentCount;
    } else {
      slot.concurrentCount = 1;
    }
  });

  // set maxDailyConcurrentCount
  if (concurrentCount > maxDailyConcurrentCount.value) {
    maxDailyConcurrentCount.value = concurrentCount;
  }

  return slots;
}

onMounted(() => {
  // debugging info
  //console.log("-----------------------");
  console.log("t" + props.debugtime);
  //console.log("now", props);
  //console.log("date", props.date);
  //console.log("dateString", props.dateString);
  //console.log("filterView", props.filterView);
  //console.log("filterPractitioners", props.filterPractitioners);
  //console.log("filterRooms", props.filterRooms);
  //console.log("earliest", earliest);
  //console.log("latest", latest);
  //console.log("rowCount", rowCount.value);
  //console.log("viewData", props.viewData);
  //console.log("viewType", props.viewType);
  //console.log("viewType", props.holidays);
  //console.log("officeVacations", props.officeVacations);

  // change oteo-now-line position every minute
  nowLineInterval = setInterval(() => {
    setNowLine();
  }, 1000 * 60);
  setNowLine();

  // hover over oteo-entry-wrapper
  let elements = document.querySelectorAll(".oteo-entry-wrapper");
  elements.forEach((element) => {
    element.addEventListener("mouseenter", (event) => {
      let id = event.target.getAttribute("data-row");
      let elements = document.querySelectorAll(
        ".oteo-entry-wrapper[data-row='" + id + "']"
      );
      elements.forEach((element) => {
        element.classList.add("hover");
      });
    });
    element.addEventListener("mouseleave", (event) => {
      let id = event.target.getAttribute("data-row");
      let elements = document.querySelectorAll(
        ".oteo-entry-wrapper[data-row='" + id + "']"
      );
      elements.forEach((element) => {
        element.classList.remove("hover");
      });
    });
  });

  // click on lock
  hideNames.value = localStorage.getItem("hideNames") === "true";
  window.addEventListener("hideNames-localstorage-changed", (event) => {
    //console.log("EVENT DETECTED", event.detail.storage);
    hideNames.value = event.detail.storage === "true";
  });
});

onUnmounted(() => {
  clearInterval(nowLineInterval);
});
</script>

<template>
  <CalendarLayout
    :date="date"
    :dateString="dateString"
    :filterView="filterView"
    :filterDayCount="parseInt(filterDayCount)"
    :filterPractitioners="filterPractitioners"
    :filterRooms="filterRooms"
    :allPractitioners="allPractitioners"
    :allRooms="allRooms"
    :holidays="holidays"
    :emergencies="emergencies"
    :officeVacations="officeVacations"
  >
    <div
      id="oteo-calendar"
      class="flex w-full h-full"
      :class="props.filterView"
    >
      <!-- stunden -->
      <div class="oteo-col flex flex-col h-full">
        <div class="oteo-header grow !border-hidden">&nbsp;</div>

        <div class="oteo-entries grow flex flex-col">
          <div
            v-for="row in (0, rowCount)"
            :key="row"
            class="oteo-entry-wrapper oteo-time-entry grow"
            :class="{
              'oteo-full-hour': isFullHour(row),
              last: row == rowCount,
            }"
            :data-row="row"
          >
            <span v-if="isFullHour(row)">{{ getTimeString(null, row) }}</span>
            <span v-else-if="office.interval == 5" class="opacity-25">{{
              getTimeString(null, row)
            }}</span>
            <span v-else>&nbsp;</span>
          </div>

          <div
            v-if="isToday(props.viewData[0].date)"
            class="oteo-now-line"
            title="Jetzt"
          ></div>
        </div>

        <div class="oteo-entry-wrapper oteo-footer shrink-0">&nbsp;</div>
        <!-- row spacer at bottom -->
      </div>

      <!-- wochenansicht -->
      <div
        class="grow flex justify-items h-full"
        v-if="props.filterView == 'woche_behandler'"
      >
        <div
          class="oteo-col grow flex flex-col h-full"
          v-for="dateColumns in props.viewData"
          :key="dateColumns.date"
          :style="{
            width: Math.max(maxDailyConcurrentCount * 35, 200) + 'px', // 35px = min-width of appointment slot
          }"
        >
          <div
            class="oteo-header truncate grow"
            :class="{
              'font-bold': props.date == dateColumns.date, // isToday(dateColumns.date),
            }"
          >
            {{ dateColumns.date_formatted }}
          </div>

          <div class="oteo-entries grow flex flex-col">
            <div
              v-for="row in (0, rowCount)"
              :key="row"
              class="oteo-entry-wrapper grow"
              :class="{
                'oteo-full-hour': isFullHour(row),
                last: row == rowCount,
              }"
              :data-row="row"
            >
              <!-- TimeSlotWeek -->
              <TimeSlot
                :timeslots="getTimeslots(row, dateColumns.date)"
                :filterView="props.filterView"
                :practitioner="null"
                :room="null"
                :hideNames="hideNames"
              />
            </div>

            <div
              v-if="isToday(dateColumns.date)"
              class="oteo-now-line"
              title="Jetzt"
            ></div>
          </div>

          <div class="oteo-entry-wrapper oteo-footer shrink-0">&nbsp;</div>
          <!-- row spacer at bottom -->
        </div>
      </div>

      <!-- tagesansicht -->
      <div class="grow flex justify-items h-full" v-else>
        <div
          class="oteo-col grow flex flex-col w-[200px] h-full"
          v-for="viewColumn in props.viewData[0].viewColumns"
          :key="viewColumn.col_id"
        >
          <div class="oteo-header truncate grow">
            {{ viewColumn.col_formatted }}
          </div>

          <div class="oteo-entries grow flex flex-col">
            <div
              v-for="row in (0, rowCount)"
              :key="row"
              class="oteo-entry-wrapper grow"
              :class="{
                'oteo-full-hour': isFullHour(row),
                last: row == rowCount,
              }"
              :data-row="row"
            >
              <!-- TimeSlot -->
              <TimeSlot
                :timeslots="
                  getTimeslots(row, props.viewData[0].date, viewColumn.col_id)
                "
                :filterView="props.filterView"
                :practitioner="viewColumn.practitioner"
                :room="viewColumn.room"
                :hideNames="hideNames"
              />
            </div>

            <div
              v-if="isToday(props.viewData[0].date)"
              class="oteo-now-line"
              title="Jetzt"
            ></div>
          </div>

          <div class="oteo-entry-wrapper oteo-footer shrink-0">&nbsp;</div>
          <!-- row spacer at bottom -->
        </div>
      </div>

      <div class="shrink-0 oteo-col flex flex-col h-full w-[22px]"></div>
      <!-- col spacer at right -->
    </div>
  </CalendarLayout>
</template>

<style lang="scss">
$oteo-calendar-header-height: 32px;
$oteo-calendar-entry-height: 22px;

#oteo-calendar {
  font-size: 14px;

  .oteo-col {
    position: relative;

    .oteo-header {
      height: $oteo-calendar-header-height;
      min-height: $oteo-calendar-header-height;
      max-height: $oteo-calendar-header-height;
      font-size: 16px;
      padding: 0 10px;
      display: flex;
      align-items: center;
      border-left: 1px solid #ccc;
    }

    .oteo-entries {
      position: relative;
    }

    .oteo-entry-wrapper {
      position: relative;
      display: flex;
      height: $oteo-calendar-entry-height;
      min-height: $oteo-calendar-entry-height;
      align-items: center;
      border-top: 1px solid #eee;
      border-left: 1px solid #ccc;

      &.oteo-time-entry {
        padding: 0 5px;
      }

      &.oteo-full-hour {
        border-top: 1px solid #ccc;
      }

      &.hover:after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: -1px;
        height: 0;
        border-top: 1px dashed #666;
        z-index: 10;
      }
    }

    .oteo-footer {
      border-top: 1px solid #ccc;
      border-left: 0px solid transparent !important;
      border-right: 0px solid transparent !important;
    }

    .oteo-now-line {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      height: 0;
      border-top: 1px solid #999;
      border-top-width: 1px;
      border-style: dashed;
      z-index: 12;
    }

    &:last-child {
      .oteo-entry-wrapper {
        border-right: 1px solid #ccc;
      }
    }
  }
}
</style>
